/* PJC v10 — Offres groupées : élément <pjc-offers> (À l'unité / Pack Duo / Pack Trio) et API window.PJC_OFFERS.
 *
 * Règles (SPEC §5) :
 *  - aucun pourcentage ni prix écrit ici : tout vient du JSON inline (prix réels des variantes, pourcentages du métaobjet pjc_offer) ;
 *  - la remise annoncée est une ESTIMATION de la remise automatique native : après l'ajout, /cart.js est relu et comparé ;
 *  - le compare_at_price des variantes n'est jamais utilisé comme prix de référence (données importées non fiables) :
 *    le « prix sans remise » d'un pack est la somme des prix réels des variantes choisies ;
 *  - aucun ajout tant que tous les articles ne sont pas choisis ; aucune variante par défaut silencieuse ;
 *  - une seule requête POST /cart/add.js (items:[{id,quantity}]), aucune propriété de ligne.
 *
 * Contrats :
 *  - émet `pjc:offer-state` (document) à chaque changement : {productId, mode, quantity, items, missing, priceCents,
 *    compareAtCents, savingsCents, currency, ready, estimated, source}
 *  - window.PJC_OFFERS = { add(), getState(), focusMissing() } (instance principale = bloc de la fiche produit)
 *  - écoute `shopify:product:select` (sélecteur de variante du thème), `pjc:purchase-change` et la quantité principale. */

const STATE_EVENT = 'pjc:offer-state';
// Valeur de StandardEvents.productSelect (@shopify/events) ; écrite en dur pour ne pas dépendre du chargement du module.
const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const SEP = String.fromCharCode(1);
const MINUS = '−';
const NBSP = ' ';
const MODE_COUNT = { single: 1, duo: 2, trio: 3 };
const MODE_TITLE = { duo: 'Pack Duo', trio: 'Pack Trio' };
// Délais des requêtes : au-delà, la requête est annulée et le panier relu avant de conclure (voir #addLines).
const REQUEST_TIMEOUT = 25000;
const BASELINE_TIMEOUT = 8000;
const ROLLBACK_TIMEOUT = 10000;
// Valeur de StandardEvents.cartLinesUpdate, utilisée si le module d'événements est indisponible.
const CART_LINES_EVENT = 'shopify:cart:lines-update';

/** Noms d'options d'import (anglais) traduits pour l'affichage. Les autres noms restent tels quels. */
const OPTION_LABELS = new Map([
  ['color', 'Couleur'],
  ['colors', 'Couleur'],
  ['colour', 'Couleur'],
  ['couleur', 'Couleur'],
  ['size', 'Taille'],
  ['sizes', 'Taille'],
  ['taille', 'Taille'],
  ['kid size', 'Taille enfant'],
  ['kids size', 'Taille enfant'],
  ['child size', 'Taille enfant'],
  ['ships from', 'Expédié depuis'],
  ['ship from', 'Expédié depuis'],
]);

const CHOOSE_PHRASES = new Map([
  ['Couleur', 'Choisir une couleur'],
  ['Taille', 'Choisir une taille'],
  ['Taille enfant', 'Choisir une taille enfant'],
  ['Expédié depuis', 'Choisir un lieu d’expédition'],
]);

const THE_PHRASES = new Map([
  ['Couleur', 'la couleur'],
  ['Taille', 'la taille'],
  ['Taille enfant', 'la taille enfant'],
  ['Expédié depuis', 'le lieu d’expédition'],
]);

/** @type {Set<PjcOffers>} */
const registry = new Set();

let eventsModule;
/** Module d'événements standard du thème (CartLinesUpdateEvent…). Null s'il est indisponible : l'ajout reste fonctionnel. */
const loadEvents = () => (eventsModule ??= import('@shopify/events').catch(() => null));

const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const variantOf = (item) => Number(item?.variant_id ?? item?.id);

/** Cumule TOUTES les lignes du panier (ou de la réponse d'ajout) portant cette variante : le même article peut figurer sur plusieurs lignes. */
function tally(items, id) {
  const total = { quantity: 0, lines: 0 };
  for (const item of Array.isArray(items) ? items : []) {
    if (variantOf(item) !== id) continue;
    total.quantity += Number(item.quantity) || 0;
    total.lines += 1;
  }
  return total;
}

const optionLabel = (name) => OPTION_LABELS.get(normalize(name)) ?? String(name);
const choosePhrase = (label) => CHOOSE_PHRASES.get(label) ?? `Choisir : ${label}`;
const thePhrase = (label) => THE_PHRASES.get(label) ?? `l’option « ${label} »`;

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

class PjcOffers extends HTMLElement {
  /** @type {AbortController | null} */
  #ac = null;
  /** @type {any} */
  #data = null;
  /** @type {Record<string, any>} */
  #refs = {};
  /** @type {Map<string, {root: HTMLElement, price: HTMLElement|null, each: HTMLElement|null, save: HTMLElement|null, sub: HTMLElement|null}>} */
  #cards = new Map();
  /** @type {{fs: HTMLElement, hint: HTMLElement, thumb: HTMLElement, img: HTMLImageElement, selects: Map<number, HTMLSelectElement>, status: HTMLElement}[]} */
  #articleEls = [];
  /** @type {{sel: string[], linked: boolean}[]} */
  #articles = [];
  #main = { sel: /** @type {string[]} */ ([]), qty: 1 };
  #mode = 'single';
  #modes = /** @type {string[]} */ ([]);
  #pct = { duo: 0, trio: 0 };
  #busy = false;
  #savingsHidden = false;
  #lastKey = '';
  #lastSummary = '';
  /** @type {any} */
  #state = null;
  /** @type {Intl.NumberFormat | null} */
  #nf = null;
  #ready = false;

  get compact() {
    return this.dataset.compact === 'true';
  }

  get state() {
    return this.#state;
  }

  connectedCallback() {
    if (!this.#ready) {
      if (!this.#setup()) return;
      this.#ready = true;
    }

    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    registry.add(this);

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    // Le panier a changé ailleurs (ligne retirée, panier vidé, autre ajout) : l'avertissement de remise ne vaut plus.
    document.addEventListener('pjc:cart-updated', this.#onPurchaseCartUpdated, { signal });
    loadEvents().then((events) => {
      if (signal.aborted) return;
      document.addEventListener(events?.StandardEvents?.cartLinesUpdate ?? CART_LINES_EVENT, this.#onCartLines, { signal });
    });

    if (!this.compact) {
      document.addEventListener(PRODUCT_SELECT_EVENT, this.#onProductSelect, { signal });
      document.addEventListener('pjc:purchase-change', this.#onPurchaseChange, { signal });
      document.addEventListener('quantity-selector:update', this.#onQuantity, { signal });
      // Le sélecteur principal peut être défini après nous : on relit son état une fois l'élément connu.
      customElements.whenDefined('variant-picker').then(() => {
        if (signal.aborted) return;
        this.#readMain();
        this.#render();
      });
    }

    // Reconnexion (élément déplacé, morph) : l'état courant est réémis et l'attribut de <html> rétabli
    // (disconnectedCallback l'a retiré ; sans cela le sélecteur de quantité resterait visible en mode pack).
    this.#lastKey = '';
    if (!this.compact && this.#state) document.documentElement.dataset.pjcOfferMode = this.#state.mode;

    this.#readMain();
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    registry.delete(this);
    if (!this.compact && document.documentElement.dataset.pjcOfferMode) {
      delete document.documentElement.dataset.pjcOfferMode;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Initialisation                                                      */
  /* ------------------------------------------------------------------ */

  #setup() {
    const script = this.querySelector('script[data-pjc-offers-json]');
    if (!script) return false;

    let raw;
    try {
      raw = JSON.parse(script.textContent || '');
    } catch {
      return false;
    }
    if (!raw || !Array.isArray(raw.variants) || raw.variants.length === 0 || !Array.isArray(raw.options)) return false;

    const names = raw.options.map(String);
    const values = names.map(() => /** @type {string[]} */ ([]));
    const seen = names.map(() => new Set());
    const index = new Map();
    for (const variant of raw.variants) {
      if (!Array.isArray(variant.o)) continue;
      variant.o.forEach((value, i) => {
        if (i < names.length && !seen[i].has(value)) {
          seen[i].add(value);
          values[i].push(value);
        }
      });
      index.set(variant.o.join(SEP), variant);
    }

    const fixed = values.map((list) => (list.length === 1 ? list[0] : null));
    const visible = names.map((_, i) => i).filter((i) => fixed[i] === null);

    const prices = raw.variants.filter((v) => v.a).map((v) => v.p);
    const allPrices = raw.variants.map((v) => v.p);
    const minPrice = Math.min(...(prices.length ? prices : allPrices));

    this.#data = {
      id: String(raw.productId),
      title: String(raw.title ?? ''),
      image: raw.image || null,
      names,
      labels: names.map(optionLabel),
      values,
      fixed,
      visible,
      variants: raw.variants,
      index,
      minPrice,
      offer: raw.offer ?? {},
    };

    this.#pct = { duo: Number(raw.offer?.duo) || 0, trio: Number(raw.offer?.trio) || 0 };
    this.#nf = this.#createFormatter();

    this.#refs = {
      detail: this.querySelector('[data-ref="detail"]'),
      howto: this.querySelector('[data-ref="howto"]'),
      articles: this.querySelector('[data-ref="articles"]'),
      summary: this.querySelector('[data-ref="summary"]'),
      error: this.querySelector('[data-ref="error"]'),
      notice: this.querySelector('[data-ref="notice"]'),
      cta: this.querySelector('[data-ref="cta"]'),
      ctaLabel: this.querySelector('[data-ref="cta-label"]'),
    };

    this.#cards.clear();
    for (const card of this.querySelectorAll('.pjc-offer-card')) {
      const mode = card.getAttribute('data-mode');
      if (!mode) continue;
      this.#cards.set(mode, {
        root: card,
        price: card.querySelector('[data-ref="price"]'),
        each: card.querySelector('[data-ref="each"]'),
        save: card.querySelector('[data-ref="save"]'),
        sub: card.querySelector('.pjc-offer-card__sub'),
      });
    }

    this.#articles = [0, 1, 2].map(() => ({ sel: names.map(() => ''), linked: true }));
    this.#main.sel = names.map(() => '');

    this.#modes = [...(this.compact ? [] : ['single']), ...['duo', 'trio'].filter((m) => this.#pct[m] > 0)];
    if (!this.#offerWindowOpen()) this.#closePacks();

    // Présélection = achat à l'unité (SPEC §5.6), même si le navigateur a restauré un autre état de formulaire.
    for (const radio of this.querySelectorAll('.pjc-offer-card__radio')) {
      radio.checked = !this.compact && radio.value === 'single';
    }
    this.#mode = 'single';

    this.#buildArticles();
    return true;
  }

  /** Même présentation que la boutique : « CHF 39.90 » (code devise, espace insécable, deux décimales, sans séparateur de milliers). */
  #createFormatter() {
    try {
      return new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
    } catch {
      return null;
    }
  }

  /** Dates de l'offre revérifiées côté navigateur (page mise en cache avant le début ou après la fin de l'offre). */
  #offerWindowOpen() {
    const { startsAt, endsAt } = this.#data.offer;
    const now = Date.now() / 1000;
    return (!startsAt || now >= startsAt) && (!endsAt || now <= endsAt);
  }

  #closePacks() {
    this.#modes = this.#modes.filter((m) => m === 'single');
    for (const mode of ['duo', 'trio']) {
      this.#cards.get(mode)?.root.remove();
      this.#cards.delete(mode);
    }
    this.#pct = { duo: 0, trio: 0 };
    if (this.#mode !== 'single') this.#mode = 'single';
  }

  #buildArticles() {
    const d = this.#data;
    const host = this.#refs.articles;
    if (!host) return;
    host.textContent = '';
    this.#articleEls = [];
    if (d.visible.length === 0) {
      host.hidden = true;
      return;
    }

    const uid = this.dataset.uid;
    for (let i = 0; i < 3; i++) {
      const fs = el('fieldset', 'pjc-offer-item');
      fs.hidden = true;
      const legend = el('legend', 'pjc-offer-item__legend');
      legend.append(document.createTextNode(`Article ${i + 1}`));
      const hint = el('span', 'pjc-offer-item__hint');
      legend.append(hint);

      const row = el('div', 'pjc-offer-item__row');
      const thumb = el('span', 'pjc-offer-item__thumb');
      const img = document.createElement('img');
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      img.decoding = 'async';
      thumb.append(img);

      const fields = el('div', 'pjc-offer-item__fields');
      const status = el('p', 'pjc-offer-item__status');
      status.id = `pjc-offer-${uid}-a${i}-status`;

      /** @type {Map<number, HTMLSelectElement>} */
      const selects = new Map();
      for (const j of d.visible) {
        const field = el('div', 'pjc-offer-item__field');
        const select = document.createElement('select');
        select.className = 'pjc-offer-item__select';
        select.id = `pjc-offer-${uid}-a${i}-o${j}`;
        select.dataset.article = String(i);
        select.dataset.option = String(j);
        select.setAttribute('aria-describedby', status.id);

        const label = el('label', 'pjc-offer-item__label', d.labels[j]);
        label.htmlFor = select.id;

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = choosePhrase(d.labels[j]);
        select.append(placeholder);
        for (const value of d.values[j]) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.dataset.base = value;
          select.append(option);
        }
        field.append(label, select);
        fields.append(field);
        selects.set(j, select);
      }

      row.append(thumb, fields);
      fs.append(legend, row, status);
      host.append(fs);
      this.#articleEls.push({ fs, hint, thumb, img, selects, status });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sélecteur principal (article 1 = variante déjà choisie)             */
  /* ------------------------------------------------------------------ */

  #root() {
    return this.closest('.shopify-section') ?? document;
  }

  #mainPicker() {
    const id = this.#data.id;
    for (const picker of this.#root().querySelectorAll('variant-picker')) {
      if (picker.dataset.productId === id && !picker.closest('product-card, quick-add-component, quick-add-dialog')) {
        return picker;
      }
    }
    return null;
  }

  /**
   * Option que le sélecteur principal considère comme non confirmée, même si un bouton est coché.
   * Source de vérité : l'attribut data-pjc-unconfirmed que pjc-buy.js pose sur le groupe d'option du sélecteur
   * (comparé au nom d'origine data-pjc-option-name), valable quel que soit le mode (unité ou pack).
   * Repli : libellés de PJC_PURCHASE.missing (mode unité).
   */
  #isUnconfirmed(index) {
    const name = normalize(this.#data.names[index]);
    for (const holder of this.#mainPicker()?.querySelectorAll('[data-pjc-unconfirmed]') ?? []) {
      if (normalize(holder.getAttribute('data-pjc-option-name')) === name) return true;
    }
    const purchase = window.PJC_PURCHASE;
    if (!purchase || String(purchase.productId) !== this.#data.id || !Array.isArray(purchase.missing)) return false;
    const names = [normalize(this.#data.names[index]), normalize(this.#data.labels[index])];
    return purchase.missing.some((entry) => names.includes(normalize(entry)));
  }

  /** @param {{name: string, value: string}[] | null} chosen */
  #applyMain(chosen) {
    const d = this.#data;
    const sel = d.names.map(() => '');
    for (const choice of chosen ?? []) {
      const i = d.names.indexOf(choice.name);
      if (i > -1 && !this.#isUnconfirmed(i) && d.values[i].includes(choice.value)) sel[i] = choice.value;
    }
    this.#main.sel = sel;
    if (this.#articles[0]?.linked) this.#articles[0].sel = sel.slice();
  }

  #readMain() {
    if (this.compact) return;
    const picker = this.#mainPicker();
    let chosen = null;
    if (picker) {
      if (typeof picker.getAllSelectedOptions === 'function') {
        chosen = picker.getAllSelectedOptions();
      } else {
        chosen = [];
        for (const input of picker.querySelectorAll('input:checked[data-option-name]')) {
          chosen.push({ name: input.dataset.optionName, value: input.value });
        }
        for (const select of picker.querySelectorAll('select')) {
          const option = select.selectedOptions[0];
          if (option?.dataset.optionName) chosen.push({ name: option.dataset.optionName, value: select.value });
        }
      }
    }
    this.#applyMain(chosen);

    const quantity = this.#root().querySelector('product-form-component input[name="quantity"]');
    const qty = Math.floor(Number(quantity?.value));
    this.#main.qty = qty > 0 ? qty : 1;
  }

  #onProductSelect = (event) => {
    const id = String(event.product?.id ?? '');
    if (!id.endsWith(`/${this.#data.id}`) && id !== this.#data.id) return;
    this.#applyMain(event.selectedOptions);
    this.#render();
    event.promise
      ?.then(() => {
        this.#readMain();
        this.#render();
      })
      .catch(() => {});
  };

  #onPurchaseChange = () => {
    this.#readMain();
    this.#render();
  };

  #onQuantity = (event) => {
    if (event.target instanceof Node && !this.#root().contains(event.target)) return;
    this.#readMain();
    this.#render();
  };

  /* ------------------------------------------------------------------ */
  /* Calculs                                                             */
  /* ------------------------------------------------------------------ */

  #money(cents) {
    const amount = cents / 100;
    return `${this.dataset.currency || 'CHF'}${NBSP}${this.#nf ? this.#nf.format(amount) : amount.toFixed(2)}`;
  }

  #variantTitle(variant) {
    const d = this.#data;
    const parts = d.visible.map((i) => variant.o[i]).filter(Boolean);
    return parts.join(' / ') || d.title;
  }

  /** @param {string[]} sel */
  #resolve(sel) {
    const d = this.#data;
    const missing = d.visible.filter((i) => !sel[i]).map((i) => d.labels[i]);
    if (missing.length) return { variant: null, missing, status: 'incomplete' };
    const key = d.names.map((_, i) => d.fixed[i] ?? sel[i]).join(SEP);
    const variant = d.index.get(key);
    if (!variant) return { variant: null, missing: [], status: 'invalid' };
    return { variant, missing: [], status: variant.a ? 'ok' : 'soldout' };
  }

  /** @param {string} mode */
  #compute(mode) {
    const d = this.#data;
    const count = MODE_COUNT[mode];

    if (mode === 'single') {
      const r = this.#resolve(this.#main.sel);
      const v = r.status === 'ok' ? r.variant : null;
      const qty = this.#main.qty;
      return {
        mode,
        quantity: qty,
        items: v ? [{ variantId: v.id, available: true, title: this.#variantTitle(v) }] : [],
        missing: r.status === 'incomplete' ? r.missing : [],
        priceCents: v ? v.p * qty : 0,
        compareAtCents: null,
        savingsCents: 0,
        ready: Boolean(v),
        estimated: !v,
        unitCents: v ? v.p : d.minPrice,
        totalCents: v ? v.p : d.minPrice,
        discountCents: 0,
        lines: v ? [{ id: v.id, quantity: qty, price: v.p }] : [],
      };
    }

    const pct = this.#pct[mode];
    /** @type {Map<number, {id: number, quantity: number, price: number}>} */
    const lines = new Map();
    const items = [];
    const missing = [];
    let unknown = 0;
    for (let i = 0; i < count; i++) {
      const r = this.#resolve(this.#articles[i].sel);
      if (r.status === 'ok') {
        const line = lines.get(r.variant.id) ?? { id: r.variant.id, quantity: 0, price: r.variant.p };
        line.quantity += 1;
        lines.set(r.variant.id, line);
        items.push({ variantId: r.variant.id, available: true, title: this.#variantTitle(r.variant) });
        continue;
      }
      unknown += 1;
      for (const label of r.missing) missing.push(`${label} (article ${i + 1})`);
      items.push({
        variantId: r.variant ? r.variant.id : null,
        available: false,
        title: r.variant ? this.#variantTitle(r.variant) : '',
      });
    }

    // QA : Shopify arrondit la remise PAR UNITÉ, le prix remisé étant arrondi au centime supérieur à égalité
    // (4490 × 15 % = 673,5 → remise 673). Même règle ici pour que l'économie annoncée soit celle du panier.
    const unitDiscount = (price) => price - Math.round((price * (100 - pct)) / 100);
    let total = unknown * d.minPrice;
    let discount = unknown * unitDiscount(d.minPrice);
    for (const line of lines.values()) {
      total += line.price * line.quantity;
      discount += unitDiscount(line.price) * line.quantity;
    }
    const hidden = this.#savingsHidden;
    return {
      mode,
      quantity: count,
      items,
      missing,
      priceCents: hidden ? total : total - discount,
      compareAtCents: hidden ? null : total,
      savingsCents: hidden ? 0 : discount,
      ready: unknown === 0,
      estimated: unknown > 0,
      unitCents: Math.round((hidden ? total : total - discount) / count),
      totalCents: total,
      discountCents: discount,
      lines: [...lines.values()],
    };
  }

  #publicState(st) {
    return {
      productId: this.#data.id,
      mode: st.mode,
      quantity: st.quantity,
      items: st.items,
      missing: st.missing,
      priceCents: st.priceCents,
      compareAtCents: st.compareAtCents,
      savingsCents: st.savingsCents,
      currency: this.dataset.currency || 'CHF',
      ready: st.ready,
      estimated: st.estimated,
      source: 'pjc-offers',
    };
  }

  /* ------------------------------------------------------------------ */
  /* Rendu                                                               */
  /* ------------------------------------------------------------------ */

  #render() {
    if (!this.#ready) return;
    const packMode = this.#mode !== 'single';

    // Plus aucune formule groupée possible (dates dépassées, page en cache) : le bloc se masque.
    this.hidden = !this.#modes.some((m) => m !== 'single');

    const computed = new Map();
    for (const mode of ['single', ...this.#modes.filter((m) => m !== 'single')]) {
      computed.set(mode, this.#compute(mode));
    }
    const current = computed.get(this.#mode) ?? this.#compute(this.#mode);

    for (const [mode, card] of this.#cards) this.#paintCard(mode, card, computed.get(mode));

    if (this.#refs.detail) this.#refs.detail.hidden = !packMode;
    if (packMode) this.#paintPack(current);

    this.#emit(current);
  }

  #paintCard(mode, card, st) {
    if (!st) return;
    const hide = this.#savingsHidden;
    if (mode === 'single') {
      if (card.price) {
        const label = st.ready ? this.#money(st.unitCents) : `dès${NBSP}${this.#money(st.unitCents)}`;
        this.#setText(card.price, label);
      }
      return;
    }
    const prefix = st.estimated ? `dès${NBSP}` : '';
    if (card.price) this.#setText(card.price, `${prefix}${this.#money(st.priceCents)}`);
    if (card.each) {
      this.#setText(card.each, hide ? '' : `soit ${prefix}${this.#money(st.unitCents)}${NBSP}l’article`);
    }
    if (card.save) {
      let text;
      if (hide) {
        text = 'Le montant exact est celui du panier.';
      } else if (st.discountCents > 0) {
        const head = st.estimated ? 'Économie dès' : 'Économie :';
        text = `${head}${NBSP}${this.#money(st.discountCents)} (${MINUS}${this.#pct[mode]}${NBSP}%)`;
      } else {
        text = '';
      }
      this.#setText(card.save, text);
    }
  }

  #setText(node, text) {
    if (node.textContent !== text) node.textContent = text;
  }

  #paintPack(st) {
    const d = this.#data;
    const mode = st.mode;
    const count = MODE_COUNT[mode];

    if (this.#refs.howto) {
      this.#setText(
        this.#refs.howto,
        d.visible.length
          ? `Choisissez ${d.visible.length > 1 ? 'les options' : thePhrase(d.labels[d.visible[0]])} de chacun des ${count} articles. Vous pouvez panacher.`
          : `Les ${count} articles sont identiques.`
      );
    }

    this.#articleEls.forEach((parts, i) => {
      parts.fs.hidden = i >= count;
      if (i >= count) return;
      this.#paintArticle(i, parts);
    });

    this.#paintSummary(st);

    this.#syncCta();
  }

  #syncCta() {
    const label = this.#refs.ctaLabel;
    if (!label || this.#mode === 'single') return;
    this.#setText(
      label,
      this.#busy ? 'Ajout en cours…' : `Ajouter le pack ${MODE_TITLE[this.#mode].replace('Pack ', '')} au panier`
    );
  }

  #paintArticle(i, parts) {
    const d = this.#data;
    const art = this.#articles[i];
    const r = this.#resolve(art.sel);

    for (const [j, select] of parts.selects) {
      if (select.value !== art.sel[j]) select.value = art.sel[j] ?? '';
      for (const option of select.options) {
        if (!option.value) continue;
        const state = this.#valueState(art.sel, j, option.value);
        const suffix = state === 'ok' ? '' : state === 'soldout' ? ' — épuisé' : ' — indisponible';
        const text = `${option.dataset.base}${suffix}`;
        if (option.textContent !== text) option.textContent = text;
        option.disabled = state !== 'ok' && option.value !== art.sel[j];
      }
    }

    const hasMainChoice = i === 0 && art.linked && !this.compact && this.#main.sel.some(Boolean);
    this.#setText(parts.hint, hasMainChoice ? ' · votre sélection' : '');

    const image = (r.variant && r.variant.i) || d.image;
    parts.thumb.hidden = !image;
    if (image && parts.img.getAttribute('src') !== image) parts.img.src = image;

    let text;
    let tone = 'neutral';
    if (r.status === 'ok') {
      text = `${this.#variantTitle(r.variant)} · ${this.#money(r.variant.p)}`;
      tone = 'ok';
    } else if (r.status === 'soldout') {
      text = `Épuisé : ${this.#variantTitle(r.variant)}. Choisissez une autre variante.`;
      tone = 'warn';
    } else if (r.status === 'invalid') {
      text = 'Cette combinaison n’existe pas. Choisissez une autre variante.';
      tone = 'warn';
    } else {
      text = `À choisir : ${r.missing.join(', ')}`;
    }
    this.#setText(parts.status, text);
    parts.status.dataset.tone = tone;
  }

  /** @returns {'ok'|'soldout'|'none'} */
  #valueState(sel, optionIndex, value) {
    const d = this.#data;
    let exists = false;
    for (const variant of d.variants) {
      if (variant.o[optionIndex] !== value) continue;
      let matches = true;
      for (const j of d.visible) {
        if (j !== optionIndex && sel[j] && variant.o[j] !== sel[j]) {
          matches = false;
          break;
        }
      }
      if (!matches) continue;
      if (variant.a) return 'ok';
      exists = true;
    }
    return exists ? 'soldout' : 'none';
  }

  #paintSummary(st) {
    const host = this.#refs.summary;
    if (!host) return;
    const count = MODE_COUNT[st.mode];
    const parts = [
      st.estimated ? `${st.mode}|e|${st.priceCents}|${st.totalCents}` : `${st.mode}|${st.priceCents}|${st.totalCents}`,
      st.discountCents,
      st.missing.join(','),
      this.#savingsHidden,
    ];
    const key = JSON.stringify(parts);
    if (key === this.#lastSummary) return;
    this.#lastSummary = key;

    host.textContent = '';
    const total = el('p', 'pjc-offers__total');
    total.append(document.createTextNode(`Prix du lot dans le panier (estimation) : `));
    const strong = el('strong', 'pjc-offers__total-price', `${st.estimated ? `dès${NBSP}` : ''}${this.#money(st.priceCents)}`);
    total.append(strong);
    if (st.compareAtCents && st.compareAtCents > st.priceCents) {
      const before = el('s', 'pjc-offers__total-before', this.#money(st.compareAtCents));
      const sr = el('span', 'pjc-offers__sr', ' au lieu de ');
      total.append(document.createTextNode(' '), sr, before);
    }
    host.append(total);

    if (this.#savingsHidden) {
      host.append(el('p', 'pjc-offers__saving', 'Le montant exact est celui du panier.'));
    } else if (st.discountCents > 0) {
      const each = this.#money(Math.round((st.totalCents - st.discountCents) / count));
      host.append(
        el(
          'p',
          'pjc-offers__saving',
          `Vous économisez ${st.estimated ? 'dès ' : ''}${this.#money(st.discountCents)} (${MINUS}${this.#pct[st.mode]}${NBSP}%), soit ${st.estimated ? 'dès ' : ''}${each} par article.`
        )
      );
    }
    if (st.missing.length) {
      host.append(el('p', 'pjc-offers__todo', `Il reste à choisir : ${st.missing.join(', ')}.`));
    }
  }

  #emit(st) {
    const detail = this.#publicState(st);
    const key = JSON.stringify(detail);
    if (key === this.#lastKey) return;
    this.#lastKey = key;
    this.#state = detail;

    if (this.compact) {
      this.dispatchEvent(new CustomEvent(STATE_EVENT, { detail }));
      return;
    }
    document.documentElement.dataset.pjcOfferMode = detail.mode;
    document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail }));
  }

  /* ------------------------------------------------------------------ */
  /* Événements d'interface                                              */
  /* ------------------------------------------------------------------ */

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.classList.contains('pjc-offer-card__radio')) {
      this.#setMode(target.value);
      return;
    }
    if (target instanceof HTMLSelectElement && target.dataset.article !== undefined) {
      const i = Number(target.dataset.article);
      const j = Number(target.dataset.option);
      const art = this.#articles[i];
      if (!art) return;
      art.sel[j] = target.value;
      if (i === 0) art.linked = false;
      this.#resetSavings();
      target.removeAttribute('aria-invalid');
      this.#clearError();
      this.#clearNotice();
      this.#render();
    }
  };

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ref="cta"]') : null;
    if (!button || !this.contains(button)) return;
    this.add();
  };

  /** Réaffiche l'économie annoncée (masquée après un écart constaté sur un ajout précédent). */
  #resetSavings() {
    if (!this.#savingsHidden) return;
    this.#savingsHidden = false;
    this.#lastSummary = '';
  }

  /** Le panier a été modifié par autre chose que ce composant : l'écart constaté ne concerne plus la sélection courante. */
  #onCartLines = (event) => {
    if (event.target instanceof Element && event.target.closest('pjc-offers')) return;
    this.#cartChanged();
  };

  /** Ajout simple (bouton principal ou barre fixe) : les ajouts de pack, eux, sont déjà traités par add(). */
  #onPurchaseCartUpdated = (event) => {
    if (event.detail?.mode && event.detail.mode !== 'single') return;
    this.#cartChanged();
  };

  #cartChanged() {
    if (!this.#savingsHidden && !this.#refs.notice?.textContent) return;
    this.#resetSavings();
    this.#clearNotice();
    this.#render();
  }

  #setMode(mode) {
    if (!MODE_COUNT[mode] || (mode !== 'single' && !this.#modes.includes(mode))) return;
    this.#mode = mode;
    this.#resetSavings();
    if (mode === 'single') {
      this.#articles[0].linked = true;
      this.#articles[0].sel = this.#main.sel.slice();
    }
    this.#clearError();
    this.#clearNotice();
    this.#render();
  }

  #setError(message, focusTarget) {
    const node = this.#refs.error;
    if (!node) return;
    node.textContent = message;
    this.#clearNotice();
    for (const select of this.querySelectorAll('select[aria-invalid]')) {
      if (select !== focusTarget) select.removeAttribute('aria-invalid');
    }
    if (focusTarget) {
      focusTarget.setAttribute('aria-invalid', 'true');
      focusTarget.setAttribute('aria-describedby', `${focusTarget.getAttribute('aria-describedby') ?? ''} ${node.id}`.trim());
    }
  }

  #clearError() {
    const node = this.#refs.error;
    if (!node || !node.textContent) return;
    node.textContent = '';
    for (const select of this.querySelectorAll('select')) {
      select.removeAttribute('aria-invalid');
      const described = (select.getAttribute('aria-describedby') ?? '').split(' ').filter((id) => id && id !== node.id);
      select.setAttribute('aria-describedby', described.join(' '));
    }
  }

  #setNotice(message) {
    const node = this.#refs.notice;
    if (!node) return;
    node.textContent = message;
  }

  #clearNotice() {
    this.#setNotice('');
  }

  /**
   * Met en évidence le premier article incomplet (focus + message lié). Retourne le message, ou '' si rien ne manque.
   * Sert à l'élément lui-même et au bouton d'achat principal (pjc-buy.js) quand un pack est sélectionné.
   */
  focusMissing() {
    if (this.#mode === 'single') return '';
    const count = MODE_COUNT[this.#mode];
    for (let i = 0; i < count; i++) {
      const r = this.#resolve(this.#articles[i].sel);
      if (r.status === 'ok') continue;
      const parts = this.#articleEls[i];
      let message;
      let target = null;
      if (r.status === 'incomplete') {
        const first = this.#data.visible.find((j) => !this.#articles[i].sel[j]);
        target = parts?.selects.get(first) ?? null;
        message = `Choisissez ${thePhrase(this.#data.labels[first])} de l’article ${i + 1}.`;
      } else {
        target = parts?.selects.values().next().value ?? null;
        message = `L’article ${i + 1} n’est pas disponible dans cette variante. Choisissez-en une autre.`;
      }
      this.#setError(message, target);
      if (target) {
        target.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        target.focus({ preventScroll: true });
      }
      return message;
    }
    return '';
  }

  /* ------------------------------------------------------------------ */
  /* Ajout au panier                                                     */
  /* ------------------------------------------------------------------ */

  /** @returns {Promise<{ok: boolean, message?: string}>} */
  async add() {
    if (this.#busy) return { ok: false, message: 'Ajout déjà en cours.' };

    if (!this.#offerWindowOpen() && this.#mode !== 'single') {
      this.#closePacks();
      this.#render();
      return this.#fail('Cette offre n’est plus disponible. Les articles peuvent toujours être achetés à l’unité.');
    }

    const st = this.#compute(this.#mode);
    if (this.#mode === 'single' && !st.ready) {
      return { ok: false, message: 'Choisissez d’abord vos options sur la fiche produit.' };
    }
    if (!st.ready) {
      const message = this.focusMissing() || 'Choisissez tous les articles du pack avant de l’ajouter.';
      return { ok: false, message };
    }

    this.#busy = true;
    this.#setBusy(true);
    this.#clearError();
    this.#clearNotice();

    try {
      return await this.#addLines(st);
    } finally {
      this.#busy = false;
      this.#setBusy(false);
    }
  }

  #setBusy(busy) {
    const cta = this.#refs.cta;
    if (!cta) return;
    cta.setAttribute('aria-busy', String(busy));
    cta.setAttribute('aria-disabled', String(busy));
    this.#syncCta();
  }

  #fail(message) {
    this.#setError(message, null);
    return { ok: false, message };
  }

  async #failWithEvent(message, code) {
    const events = await loadEvents();
    if (events?.CartErrorEvent) {
      this.dispatchEvent(new events.CartErrorEvent({ error: message, code }));
    }
    return this.#fail(message);
  }

  #sectionIds() {
    const ids = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    }
    return [...ids].slice(0, 5).join(',');
  }

  /**
   * POST JSON annulable : la requête est abandonnée après `timeout` ms (AbortError), corps de réponse compris.
   * @returns {Promise<{response: Response, data: any}>}
   */
  async #postJson(url, body, timeout = REQUEST_TIMEOUT) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => null);
      if (controller.signal.aborted) throw new DOMException('Délai dépassé', 'AbortError');
      return { response, data };
    } finally {
      window.clearTimeout(timer);
    }
  }

  /** Relit /cart.js (annulable). Retourne null si la lecture échoue ou dépasse le délai. */
  async #readCart(timeout = REQUEST_TIMEOUT) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(this.dataset.cartUrl || '/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      const cart = response.ok ? await response.json() : null;
      return controller.signal.aborted ? null : cart;
    } catch {
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * Après une coupure ou un délai dépassé, la requête d'ajout a pu aboutir côté serveur. On compare le panier relu
   * au panier d'avant l'ajout : 'full' (tout est dedans), 'none' (rien), 'partial' ou 'unknown' (pas de point de comparaison).
   * @returns {{state: 'full'|'none'|'partial'|'unknown', deltas: {id: number, amount: number}[]}}
   */
  #reconcile(cart, baseline, lines) {
    if (!cart || !Array.isArray(cart.items) || !baseline || !Array.isArray(baseline.items)) {
      return { state: 'unknown', deltas: [] };
    }
    const deltas = lines.map((line) => ({
      id: line.id,
      requested: line.quantity,
      amount: tally(cart.items, line.id).quantity - tally(baseline.items, line.id).quantity,
    }));
    const full = deltas.every((d) => d.amount >= d.requested);
    const none = deltas.every((d) => d.amount <= 0);
    return { state: full ? 'full' : none ? 'none' : 'partial', deltas };
  }

  async #addLines(st) {
    const lines = st.lines;
    const body = { items: lines.map((line) => ({ id: line.id, quantity: line.quantity })) };
    const sections = this.#sectionIds();
    if (sections) body.sections = sections;

    // Point de comparaison : si la requête d'ajout est coupée (délai, réseau), on saura ce qui a réellement été ajouté.
    const baseline = await this.#readCart(BASELINE_TIMEOUT);

    let result = null;
    try {
      result = await this.#postJson(this.dataset.cartAddUrl || '/cart/add.js', body);
    } catch {
      result = null;
    }

    let data = null;
    let added = [];
    let cart;

    if (!result) {
      // Délai dépassé ou coupure : la requête a peut-être abouti. On relit le panier AVANT de conclure à un échec.
      cart = await this.#readCart();
      const outcome = this.#reconcile(cart, baseline, lines);
      if (outcome.state === 'none') {
        return this.#failWithEvent(
          'Connexion impossible : aucun article n’a été ajouté. Vérifiez votre réseau puis réessayez.',
          'SERVICE_UNAVAILABLE'
        );
      }
      if (outcome.state === 'unknown') {
        return this.#failWithEvent(
          'La connexion a été interrompue et le résultat de l’ajout est incertain. Vérifiez votre panier avant de réessayer.',
          'SERVICE_UNAVAILABLE'
        );
      }
      if (outcome.state === 'partial') {
        await this.#rollback(cart.items, outcome.deltas.filter((d) => d.amount > 0));
        return this.#failWithEvent(
          'Le pack n’a pas pu être ajouté en entier : le panier a été remis dans son état précédent. Réessayez ou choisissez d’autres variantes.',
          'INVALID'
        );
      }
      added = cart.items;
    } else {
      const { response } = result;
      data = result.data;
      if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
        const status = Number(data?.status ?? response.status);
        const detail = typeof data?.description === 'string' ? data.description.trim() : '';
        const message =
          status === 422
            ? `Ajout impossible : stock insuffisant pour cette sélection${detail ? ` (${detail})` : ''}. Aucun article n’a été ajouté. Choisissez une autre variante ou réduisez les quantités identiques.`
            : `L’ajout au panier a échoué (code ${status || 'inconnu'}). Aucun article n’a été ajouté. Réessayez dans un instant.`;
        return this.#failWithEvent(message, 'INVALID');
      }

      // Aucun ajout partiel : chaque variante demandée doit figurer dans la réponse (toutes lignes cumulées) avec au moins la quantité demandée.
      added = Array.isArray(data?.items) ? data.items : [];
      const shortfall = lines.some((line) => tally(added, line.id).quantity < line.quantity);
      if (shortfall) {
        await this.#rollback(added, lines.map((line) => ({ id: line.id, amount: line.quantity })));
        return this.#failWithEvent(
          'Le pack n’a pas pu être ajouté en entier : le panier a été remis dans son état précédent. Réessayez ou choisissez d’autres variantes.',
          'INVALID'
        );
      }

      cart = await this.#readCart();
    }

    let verified = false;
    let received = 0;
    if (cart && Array.isArray(cart.items)) {
      const missingLine = lines.some((line) => tally(cart.items, line.id).quantity < line.quantity);
      if (missingLine) {
        await this.#rollback(added, lines.map((line) => ({ id: line.id, amount: line.quantity })));
        return this.#failWithEvent(
          'Le panier ne contient pas tous les articles du pack : l’ajout a été annulé. Réessayez.',
          'INVALID'
        );
      }
      if (st.mode !== 'single') {
        const check = this.#checkDiscount(cart, lines, this.#pct[st.mode]);
        verified = check.verified;
        received = check.received;
      }
    }

    await this.#announce(cart, data, st);

    if (st.mode === 'single') return { ok: true, message: 'Article ajouté au panier.' };

    let message;
    if (verified) {
      message = `${MODE_TITLE[st.mode]} ajouté au panier. Remise appliquée : ${MINUS}${this.#money(received)}.`;
    } else {
      this.#savingsHidden = true;
      this.#lastSummary = '';
      message = `${MODE_TITLE[st.mode]} ajouté au panier. Le montant exact est celui du panier.`;
    }
    this.#setNotice(message);
    this.#render();
    return { ok: true, message };
  }

  /**
   * Compare la remise reçue dans /cart.js à la remise attendue. Toutes les lignes du panier portant les variantes du pack
   * sont cumulées. Shopify arrondit soit sur le total de la ligne, soit à l'unité puis ×quantité : les deux modes sont
   * acceptés, avec une tolérance d'1 centime PAR LIGNE de panier comparée (jamais 1 centime sur l'ensemble).
   */
  #checkDiscount(cart, lines, pct) {
    let expectedLine = 0;
    let expectedUnit = 0;
    let received = 0;
    let count = 0;
    let valid = true;
    for (const line of lines) {
      for (const item of cart.items) {
        if (variantOf(item) !== line.id) continue;
        const quantity = Number(item.quantity);
        const original = Number(item.original_line_price);
        const final = Number(item.final_line_price);
        const unit = Number.isFinite(Number(item.original_price)) ? Number(item.original_price) : original / quantity;
        if (![quantity, original, final, unit].every(Number.isFinite) || quantity <= 0) {
          valid = false;
          continue;
        }
        expectedLine += Math.round((original * pct) / 100);
        // Même arrondi que l'estimation (voir #compute) : remise par unité, prix remisé arrondi au centime supérieur à égalité.
        expectedUnit += (unit - Math.round((unit * (100 - pct)) / 100)) * quantity;
        received += original - final;
        count += 1;
      }
    }
    const near = (expected) => Math.abs(expected - received) <= count;
    return { verified: valid && count > 0 && (near(expectedLine) || near(expectedUnit)), received, expected: expectedLine };
  }

  /**
   * Retire des lignes du panier les quantités ajoutées par erreur. `items` : lignes du panier ou de la réponse d'ajout ;
   * `amounts` : quantité à retirer par variante. Meilleur effort : le message d'erreur demande de vérifier le panier.
   */
  async #rollback(items, amounts) {
    const changeUrl = this.dataset.cartChangeUrl || '/cart/change.js';
    for (const { id, amount } of amounts) {
      const item = items.find((i) => variantOf(i) === id && i.key);
      if (!item) continue;
      const previous = Math.max(0, Number(item.quantity) - amount);
      try {
        await this.#postJson(changeUrl, { id: item.key, quantity: previous }, ROLLBACK_TIMEOUT);
      } catch {
        // Meilleur effort : le message d'erreur demande de vérifier le panier.
      }
    }
  }

  /** Prévient le thème (tiroir panier, pastille, barre fixe) comme le fait le formulaire produit. */
  async #announce(cart, data, st) {
    const events = await loadEvents();
    const detail = {
      items: cart?.items,
      source: 'pjc-offers',
      sourceId: this.id || `pjc-offers-${this.dataset.uid}`,
      itemCount: st.quantity,
      productId: this.#data.id,
      sections: data?.sections,
      didError: false,
    };
    const lines = st.lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity }));
    const payload = {
      cart: cart && events ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail,
    };
    const promise = Promise.resolve(payload);
    if (events?.CartLinesUpdateEvent) {
      this.dispatchEvent(new events.CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise }));
      return;
    }
    const fallback = new Event('shopify:cart:lines-update', { bubbles: true, cancelable: true });
    Object.assign(fallback, { action: 'add', context: 'product', lines, promise });
    this.dispatchEvent(fallback);
  }
}

function primary() {
  for (const instance of registry) {
    if (!instance.compact) return instance;
  }
  return null;
}

if (!window.PJC_OFFERS) {
  window.PJC_OFFERS = {
    add: () =>
      primary()?.add() ??
      Promise.resolve({ ok: false, message: 'Aucune offre groupée n’est disponible sur cette page.' }),
    getState: () => primary()?.state ?? null,
    focusMissing: () => primary()?.focusMissing() ?? '',
  };
}

if (!customElements.get('pjc-offers')) {
  customElements.define('pjc-offers', PjcOffers);
}
